import React, { useState, useEffect } from 'react';
import { 
  Search,
  Plus,
  Building2,
  Users,
  GraduationCap,
  Mail,
  Phone,
  MapPin,
  MoreVertical,
  FileText,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { useTheme } from "../../context/ThemeContext";
import { SkeletonStudioCard, SkeletonStatCard } from '../../components/ui/SkeletonLoader';

// Mock data for demonstration
const mockStudios = [
  {
    id: 1,
    name: "Elite Dance Academy",
    location: "New York, NY",
    director: "Sarah Johnson",
    email: "sarah@elitedance.com",
    phone: "(555) 123-4567",
    studentCount: 145,
    teacherCount: 12,
    status: "active",
    lastCompetition: "2024 Spring Showcase"
  },
  {
    id: 2,
    name: "Rhythm Works Studio",
    location: "Los Angeles, CA",
    director: "Michael Chen",
    email: "michael@rhythmworks.com",
    phone: "(555) 987-6543",
    studentCount: 98,
    teacherCount: 8,
    status: "active",
    lastCompetition: "2024 Winter Festival"
  },
  {
    id: 3,
    name: "Dance Fusion Center",
    location: "Chicago, IL",
    director: "Emily Rodriguez",
    email: "emily@dancefusion.com",
    phone: "(555) 456-7890",
    studentCount: 167,
    teacherCount: 15,
    status: "inactive",
    lastCompetition: "2023 Fall Competition"
  }
];

const StudiosManager = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudio, setSelectedStudio] = useState(null);
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isLoading, setIsLoading] = useState(true);

  // Simulate loading state - removing timer and setting directly in a real fetch
  useEffect(() => {
    // In a real implementation, you would fetch data here
    // and set isLoading to false when the data is received
    const fetchStudios = async () => {
      try {
        // Replace with actual API call
        // const response = await fetchWithAuth(STUDIOS_API);
        // setStudios(response.studios);
        
        // For demo purposes, simulate loading delay
        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
      } catch (error) {
        console.error("Error fetching studios:", error);
        setIsLoading(false);
      }
    };
    
    fetchStudios();
  }, []);

  // Filter studios based on search term
  const filteredStudios = mockStudios.filter(studio => 
    studio.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    studio.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
    studio.director.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const StudioCard = ({ studio }) => (
    <div 
      className={`card shadow-lg hover:shadow-xl transition-all cursor-pointer
        ${selectedStudio?.id === studio.id ? 'ring-2 ring-primary' : ''}
        ${isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'bg-base-100'}`}
      onClick={() => setSelectedStudio(studio)}
    >
      <div className="card-body">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              isDarkMode ? 'bg-primary/20' : 'bg-primary/10'
            }`}>
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">{studio.name}</h3>
              <div className="flex items-center text-sm text-base-content/70">
                <MapPin className="w-4 h-4 mr-1" />
                {studio.location}
              </div>
            </div>
          </div>
          <div className="dropdown dropdown-end">
            <label tabIndex={0} className="btn btn-ghost btn-sm btn-circle">
              <MoreVertical className="w-4 h-4" />
            </label>
            <ul tabIndex={0} className={`dropdown-content z-[1] menu p-2 shadow rounded-box w-52 ${
              isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'bg-base-100'
            }`}>
              <li><a><FileText className="w-4 h-4" />View Details</a></li>
              <li><a><ExternalLink className="w-4 h-4" />Visit Portal</a></li>
            </ul>
          </div>
        </div>

        <div className="divider my-2"></div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            <span className="text-sm">{studio.studentCount} Students</span>
          </div>
          <div className="flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-primary" />
            <span className="text-sm">{studio.teacherCount} Teachers</span>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Mail className="w-4 h-4 text-base-content/70" />
            <span>{studio.email}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Phone className="w-4 h-4 text-base-content/70" />
            <span>{studio.phone}</span>
          </div>
        </div>

        <div className="mt-4">
          <div className={`badge badge-outline ${isDarkMode ? 'border-white/20' : ''}`}>
            Last Event: {studio.lastCompetition}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`flex justify-between items-center p-6 rounded-box ${
        isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-xl' : 'bg-base-100 border border-base-300'
      }`}>
        <div>
          <h1 className="text-3xl font-bold">Studios Manager</h1>
          <p className="text-base-content/70 mt-1">Manage and monitor all connected dance studios</p>
        </div>
        <button className="btn btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Add New Studio
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        {isLoading ? (
          <React.Fragment>
            <SkeletonStatCard />
            <SkeletonStatCard />
          </React.Fragment>
        ) : (
          <div className={`rounded-xl shadow ${isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'card bg-base-100'}`}>
            <div className={`stat rounded-box ${isDarkMode ? 'bg-transparent' : 'bg-base-100'}`}>
              <div className="stat-title">Total Studios</div>
              <div className="stat-value text-primary">{mockStudios.length}</div>
              <div className="stat-desc">↗︎ 2 new this month</div>
            </div>
          </div>
        )}

        <div className={`rounded-xl shadow ${isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' : 'card bg-base-100'}`}>
          <div className={`stat rounded-box ${isDarkMode ? 'bg-transparent' : 'bg-base-100'}`}>
            <div className="stat-title">Active Studios</div>
            <div className="stat-value text-success">
              {mockStudios.filter(s => s.status === 'active').length}
            </div>
            <div className="stat-desc">Active in competitions</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className={`p-6 rounded-box ${
        isDarkMode ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-xl' : 'bg-base-100 border border-base-300'
      }`}>
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50" />
            <input
              type="text"
              placeholder="Search studios by name, location, or director..."
              className="input input-bordered w-full pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select className="select select-bordered w-48">
            <option>All Statuses</option>
            <option>Active</option>
            <option>Inactive</option>
          </select>
          <select className="select select-bordered w-48">
            <option>Sort By</option>
            <option>Name</option>
            <option>Location</option>
            <option>Student Count</option>
          </select>
        </div>
      </div>

      {/* Studios Grid */}
      {!isLoading && filteredStudios.length === 0 ? (
        <div className={`p-4 rounded-lg ${
          isDarkMode 
            ? 'bg-blue-500/10 border border-blue-500/20 text-blue-400' 
            : 'alert'
        }`}>
          <div className="flex items-center">
            <AlertCircle className={`h-5 w-5 ${isDarkMode ? 'text-blue-400' : 'text-info'} mr-2`} />
            <p className="text-sm">
              No studios found. Try adjusting your search criteria.
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            <React.Fragment>
              <SkeletonStudioCard />
              <SkeletonStudioCard />
              <SkeletonStudioCard />
            </React.Fragment>
          ) : (
            filteredStudios.map((studio) => (
              <StudioCard
                key={studio.id}
                studio={studio}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default StudiosManager;